import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'wouter'
import {
    Alert,
    Box,
    Checkbox,
    FormControlLabel,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import VpnLockIcon from '@mui/icons-material/VpnLock'
import { z } from 'zod'
import DashboardLayout from '../components/DashboardLayout'
import SelectOrgs from '@components/SelectOrgs'
import SsoConnectionFormFields from '@components/SsoConnectionFormFields'
import {
    attributeMapFromFields,
    CONNECTION_ID_ERROR,
    CONNECTION_ID_REGEX,
    defaultSsoFieldsValues,
    normalizeUsernameAttribute,
    parseEmailDomains,
    payloadForMode,
    ssoFieldsObjectSchema,
    ssoModeRefine,
    SsoFieldsValues,
} from '@components/SsoConnectionFormFields.helpers'
import useSession from '@hooks/useSession'
import { HASsoResult, SsoConnection } from '@types'

// Phase 2 — Step 3 (revised in Step 4 to use the shared
// SsoConnectionFormFields component so Edit can pick up the same
// fields without copy-paste).
//
// Single-mode create form (entityID + SSO URL + signing cert
// triple). The other two input modes (metadata URL / XML paste)
// land in Step 5 inside the shared component itself, at which
// point both Create and Edit get them for free.
//
// Flow:
//   /manage-sso-connections (list) -> "+ New connection"
//     -> /create-sso-connection?org=<orgId>  (this page)
//     -> on submit, POST /api/ha/v2/sso-connections (proxied to
//        habitat-admin's /v2/sso-connections POST, gated by
//        x-staff-api-key)
//     -> on success, navigate to
//        /manage-sso-connections?org=<orgId>&created=<slug>.
//        The list page reads `?created=` once, fires its own
//        success toast, then strips the param so a refresh can't
//        re-toast.

const SUBMIT_URL = '/api/ha/v2/sso-connections'

// Page-level fields (org picker + editable slug). Composed with the
// shared `ssoFieldsSchema` below to form the full form schema.
const pageSchema = z.object({
    org_id: z.string().min(1, 'Pick an organisation.'),
    connection_id: z
        .string()
        .min(1, 'Connection ID is required.')
        .max(64, 'Connection ID is too long (max 64 chars).')
        .regex(CONNECTION_ID_REGEX, CONNECTION_ID_ERROR),
})

// `ssoFieldsObjectSchema` is the un-refined raw `ZodObject` so we
// can `.merge()` it with the page-level fields here, then apply
// the mode refine on the merged result.
const formSchema = pageSchema
    .merge(ssoFieldsObjectSchema)
    .superRefine(ssoModeRefine)
type FormValues = z.infer<typeof formSchema>

const defaultForm = (orgId: string | undefined): FormValues => ({
    org_id: orgId ?? '',
    connection_id: '',
    ...defaultSsoFieldsValues(),
})

export default function CreateSsoConnectionPage() {
    const session = useSession()!
    const [, setLocation] = useLocation()

    // Wouter's `useLocation` doesn't carry query strings — read the
    // pre-fill `?org=` straight off `window.location` once on mount.
    const initialOrgId = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('org') ?? undefined
    }, [])

    const [form, setForm] = useState<FormValues>(() =>
        defaultForm(initialOrgId)
    )
    const [errors, setErrors] = useState<
        Partial<Record<keyof FormValues, string>>
    >({})
    const [submitting, setSubmitting] = useState(false)
    const [topError, setTopError] = useState<string | null>(null)

    // Domain-ownership confirmation gate. Same shape as
    // OnboardSsoTenantPage — see the rationale comment there.
    const [domainsConfirmed, setDomainsConfirmed] = useState(false)

    // Track whether the user has touched any field beyond the
    // pre-filled `org_id`. Used to gate the Cancel button behind a
    // confirm so a misclick doesn't silently discard a half-typed
    // form. Comparing the whole form to its default catches all
    // fields incl. flag toggles.
    const isDirty = useMemo(() => {
        const baseline = defaultForm(initialOrgId)
        return Object.keys(form).some(
            (key) =>
                form[key as keyof FormValues] !==
                baseline[key as keyof FormValues]
        )
    }, [form, initialOrgId])

    // Re-validate on any form change so the submit button enables /
    // disables in real time. Same pattern as CreateNewOrgPage but
    // synchronous — no debounce. Single memoised parse feeds both the
    // submit gate and the per-field error effect (the schema carries
    // a superRefine plus a per-domain refine, so we don't want to run
    // it twice per render).
    const parsedForm = useMemo(() => formSchema.safeParse(form), [form])
    const formValid = parsedForm.success

    const parsedDomains = useMemo(
        () => parseEmailDomains(form.email_domains_raw ?? ''),
        [form.email_domains_raw]
    )
    const hasDomains = parsedDomains.length > 0
    const submitDisabled =
        !formValid || submitting || (hasDomains && !domainsConfirmed)

    useEffect(() => {
        if (parsedForm.success) {
            setErrors({})
            return
        }
        const next: Partial<Record<keyof FormValues, string>> = {}
        for (const issue of parsedForm.error.issues) {
            const field = issue.path[0] as keyof FormValues
            if (!next[field]) next[field] = issue.message
        }
        setErrors(next)
    }, [parsedForm])

    // Re-confirm ownership whenever the claimed domain set changes —
    // otherwise an operator could tick the box for one set of domains,
    // edit the field, and submit against an unconfirmed value, which
    // defeats the gate. Keyed on the normalised domain list so cosmetic
    // edits (case, spacing, ordering of duplicates) don't churn it.
    const domainsKey = parsedDomains.join(',')
    useEffect(() => {
        setDomainsConfirmed(false)
    }, [domainsKey])

    const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }))

    // Sub-typed setter for the shared component — only touches the
    // SsoFieldsValues subset of the form. Avoids leaking the page-
    // level schema into the shared component's prop type.
    const setSharedField = <K extends keyof SsoFieldsValues>(
        key: K,
        value: SsoFieldsValues[K]
    ) => set(key as keyof FormValues, value as FormValues[keyof FormValues])

    const cancelToList = () => {
        if (
            isDirty &&
            !window.confirm(
                'Discard this form? Anything you typed will be lost.'
            )
        ) {
            return
        }
        setLocation(
            form.org_id
                ? `/manage-sso-connections?org=${encodeURIComponent(form.org_id)}`
                : '/manage-sso-connections'
        )
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        // Enforce the domain-ownership gate here too, not just via the Submit
        // button's `disabled` prop — the submission path owns the gate, so it
        // can't be bypassed by any other submit trigger.
        if (!formValid || submitting || (hasDomains && !domainsConfirmed))
            return

        setSubmitting(true)
        setTopError(null)

        const emailDomains = parseEmailDomains(form.email_domains_raw ?? '')

        // The mode-specific columns (`idp_signing_cert`,
        // `idp_metadata_url`, `idp_metadata_xml`) — `payloadForMode`
        // returns the active one and nulls the other two so a row
        // never carries stale data from a mode the staff didn't
        // ultimately pick.
        const modeFields = payloadForMode(form)

        const payload = {
            org_id: form.org_id,
            connection_id: form.connection_id,
            provider_type: 'saml' as const,
            idp_entity_id: form.idp_entity_id,
            idp_sso_url: form.idp_sso_url,
            idp_signing_cert: modeFields.idp_signing_cert,
            idp_metadata_url: modeFields.idp_metadata_url,
            idp_metadata_xml: modeFields.idp_metadata_xml,
            nameid_format: form.nameid_format,
            attribute_map: attributeMapFromFields(form),
            username_attribute: normalizeUsernameAttribute(
                form.username_attribute
            ),
            allow_idp_initiated: form.allow_idp_initiated,
            enforce_sso: form.enforce_sso,
            enabled: form.enabled,
            require_response_signature: form.require_response_signature,
            email_domains: emailDomains,
        }

        try {
            const response = await fetch(SUBMIT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.accessToken}`,
                },
                body: JSON.stringify(payload),
            })

            const body = (await response.json()) as HASsoResult<SsoConnection>

            if (!response.ok || body.error) {
                const message =
                    !response.ok && !body.error
                        ? `HTTP ${response.status}`
                        : body.error
                          ? body.message
                          : 'Unknown error'
                setTopError(`Failed to create connection: ${message}`)
                setSubmitting(false)
                return
            }

            // Hand the success message off to the list page via the
            // URL — the list page reads `?created=<slug>` once on
            // mount, fires its own toast, then strips the param so
            // a refresh doesn't re-toast. This avoids the ordering
            // bug where this page's SnackbarProvider would unmount
            // before notistack could render the toast.
            setLocation(
                `/manage-sso-connections?org=${encodeURIComponent(
                    form.org_id
                )}&created=${encodeURIComponent(body.result.connection_id)}`
            )
        } catch (err) {
            console.error(err)
            setTopError(
                err instanceof Error
                    ? err.message
                    : 'Failed to create connection'
            )
            setSubmitting(false)
        }
    }

    // Inline-Alert-not-DashboardErrorView: previously a `topError`
    // unmounted the entire form and rendered the dashboard's
    // generic error view, which threw away the user's typed
    // payload. On a transient backend hiccup (502 from the proxy,
    // dropped staff API key, validation message that needed a
    // small edit) the operator had to re-enter every field. Render
    // the error inline at the top of the form instead so the
    // payload survives the failure and the staff can fix the
    // issue + retry.

    return (
        <DashboardLayout>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 4,
                    paddingBottom: 8,
                }}
            >
                <Paper sx={{ p: 4, width: '100%', maxWidth: 720 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ mb: 1 }}
                    >
                        <VpnLockIcon color="primary" />
                        <Typography variant="h6">
                            Create SSO connection
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'gray', mb: 3 }}>
                        Use this form when the IdP operator has given you the
                        entity ID, SSO URL, and signing certificate directly
                        (the most common Shibboleth setup), or switch the IdP
                        configuration source to supply a metadata URL or pasted
                        metadata XML instead. All flags default to{' '}
                        <strong>off</strong> — flip <code>enabled</code> on
                        after the connection has been round-tripped end-to-end.
                    </Typography>

                    {topError && (
                        <Alert
                            severity="error"
                            onClose={() => setTopError(null)}
                            sx={{ mb: 2 }}
                        >
                            {topError}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            <Box>
                                <SelectOrgs
                                    selectedOrg={form.org_id || undefined}
                                    setSelectedOrg={(value) =>
                                        set(
                                            'org_id',
                                            (typeof value === 'function'
                                                ? value(form.org_id)
                                                : value) ?? ''
                                        )
                                    }
                                    required
                                />
                                {errors.org_id && (
                                    <Typography variant="caption" color="error">
                                        {errors.org_id}
                                    </Typography>
                                )}
                            </Box>

                            <TextField
                                label="Connection ID (slug)"
                                helperText={
                                    errors.connection_id ??
                                    'Lowercase alphanumeric + hyphens. Becomes part of the public login URL: /sso/saml/<slug>/login'
                                }
                                error={!!errors.connection_id}
                                value={form.connection_id}
                                onChange={(e) =>
                                    set('connection_id', e.target.value)
                                }
                                required
                                fullWidth
                            />

                            <SsoConnectionFormFields
                                values={form}
                                setValue={setSharedField}
                                errors={errors}
                            />

                            {hasDomains && (
                                <Alert
                                    severity={
                                        domainsConfirmed ? 'success' : 'warning'
                                    }
                                    sx={{ mt: 2 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={domainsConfirmed}
                                                onChange={(e) =>
                                                    setDomainsConfirmed(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2">
                                                    I confirm that this tenant
                                                    owns the email domain(s)
                                                    entered above.
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Login-form discovery routes
                                                    any user with an email at
                                                    these domains to this
                                                    tenant's IdP. Claiming a
                                                    domain a tenant doesn't own
                                                    silently breaks SSO for the
                                                    legitimate owner. See{' '}
                                                    <code>
                                                        docs/onboard-tenant.md
                                                    </code>{' '}
                                                    §1.4.
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                </Alert>
                            )}

                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                spacing={2}
                            >
                                <LoadingButton
                                    onClick={cancelToList}
                                    color="inherit"
                                >
                                    Cancel
                                </LoadingButton>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={submitting}
                                    disabled={submitDisabled}
                                    startIcon={<VpnLockIcon />}
                                >
                                    Create connection
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    </form>
                </Paper>
            </Box>
        </DashboardLayout>
    )
}
